// パリィの火花 形B「溶けた鉄の滴」(DEV ?sparkShape=b・2026-10-23)。
//   10-23 ユーザー「形を変えるなら、どれが良いですか：Bを見たい。変だったら今の形でいい」。
//   見本の絵 = そざい/concept/parry-spark-1022/b_molten.png (Git 管理外・main のチェックアウト)。 4 コマで読んだ物:
//     1 コマ目 = 接点に星形の光と細かい飛沫 / 2 コマ目 = 重い滴が主人公の側へ弧を描いて落ちる /
//     3 コマ目 = 滴が床で跳ね、小さな飛沫が上がる / 4 コマ目 = 床に残った滴が燠のように冷めて消える。
//   見本の 1 コマ目の縦の長い光のすじは描かない (線を描かない = 10-10 の決まり)。 星の光の腕は短い先細りだけ。
//   明滅しない (どれも濃さは上がって下がるだけ)。 色は ?sparkMix に従う (今 = 金→橙・青白7割 = 7 割が白→水色→青)。
//   すじ・火の粉 (drawTehonStreaks・drawTehonEmbers) は形B の間は描かない。 接点の光の塊・画面の明るさ・床の照りはそのまま。
//   記録 = docs/research/2026-10-23-parry-spark-blue70-b.md
import {
  BLUE_RAMP, MOLTEN_END, SPARK_RAMP, TEHON_COLOR, h01, rgba, sparkBlueShare, sparkIsBlue, sparkRampAt, tehonSeed, tehonSparkShape,
} from './parry_tehon_fx'
import type { FxEvent } from './types'

type Spark = Extract<FxEvent, { kind: 'parry_spark' }>
type RGB = readonly [number, number, number]
type Ramp = typeof SPARK_RAMP

const clamp01 = (t: number) => Math.max(0, Math.min(1, t))

/** 形B の値。 */
export const MOLTEN = {
  /** 星形の光: rise 秒で満ち、hold 秒まで保って end 秒で消える。 腕の長さ (横・縦・斜め px)。 */
  star: { rise: 0.012, hold: 0.03, end: 0.1, long: 38, short: 24, diag: 15, width: 3.2, tilt: -0.2, core: 13 },
  /** 細かい飛沫 (1 コマ目に接点のまわりへ散る細かい粒)。 */
  spray: { count: 30, born: 0.025, speed: [450, 1250] as const, drag: 7, gravity: 700, life: [0.07, 0.18] as const, size: [0.7, 1.3] as const, back: 0.55 },
  /** 重い滴。 主人公の側 (後ろ) へ弧を描いて落ち、床で跳ねる (bounce 回まで・跳ね返りの強さ rest)、 その後は床に残って冷める。 */
  drop: { count: 12, born: 0.055, speed: [240, 640] as const, drag: 0.9, gravity: 3000, radius: [2, 4.4] as const,
    bounce: 2, restitution: 0.3, keepX: 0.6, minBounce: 140, fwd: 0.2 },
  /** 床で跳ねた時の小さな飛沫。 */
  splash: { count: 4, speed: [120, 340] as const, gravity: 1500, life: [0.09, 0.17] as const, flash: 0.07 },
  /** 全体が消え切る前の、ゆるやかな引き (fadeFrom 秒から MOLTEN_END 秒へ)。 */
  fadeFrom: 0.68,
} as const

type Widen<T> = { readonly [K in keyof T]: T[K] extends number ? number : T[K] extends readonly [number, number] ? readonly [number, number] : Widen<T[K]> }
export type MoltenParams = Widen<typeof MOLTEN>
/** 形B の強め (DEV ?sparkShape=b2・10-23)。 形B は派手さが小さい恐れ (星が 0.1 秒で消える・滴が 2〜4px) への比較版。
 *  目安 = 今の形 (すじ 84 本・火の粉 50 粒) と並べて見劣りしない量。 星は長く大きく、飛沫は倍以上で遠くまで、滴は大きく倍。 線は描かない (腕は先細りのまま)。 */
export const MOLTEN_STRONG: MoltenParams = {
  star: { rise: 0.012, hold: 0.07, end: 0.24, long: 72, short: 46, diag: 30, width: 5.4, tilt: -0.2, core: 26 },
  spray: { count: 80, born: 0.03, speed: [900, 2700], drag: 5, gravity: 700, life: [0.14, 0.34], size: [1.1, 2.1], back: 0.55 },
  drop: { count: 26, born: 0.06, speed: [240, 640], drag: 0.9, gravity: 3000, radius: [3.2, 7],
    bounce: 2, restitution: 0.3, keepX: 0.6, minBounce: 140, fwd: 0.2 },
  splash: { count: 6, speed: [140, 420], gravity: 1500, life: [0.12, 0.22], flash: 0.1 },
  fadeFrom: 0.68,
}
/** 今の形の値 (b = MOLTEN・b2 = MOLTEN_STRONG)。 */
export function moltenP(): MoltenParams { return tehonSparkShape() === 'b2' ? MOLTEN_STRONG : MOLTEN }

const DT = 1 / 240

export type DropSpec = { born: number; vx: number; vy: number; r: number; ox: number; oy: number }
/** i 粒目の重い滴 (x の + = 主人公の側。 呼ぶ側で向きを掛ける)。 */
export function moltenDropSpec(i: number, seed: number, P: MoltenParams = moltenP()): DropSpec {
  const D = P.drop
  const r = (k: number) => h01(i * k + seed + 31.7)
  const deg = -40 + 88 * r(2.1)   // 0 = 真横・負 = 上向き (上へ高く上がりすぎると床に残る間が無くなる)
  const side = r(2.9) < D.fwd ? -1 : 1
  const v = D.speed[0] + (D.speed[1] - D.speed[0]) * r(3.7)
  const a = deg * Math.PI / 180
  return {
    born: 0.008 + D.born * Math.pow(r(1.3), 1.5),
    vx: Math.cos(a) * v * side, vy: Math.sin(a) * v,
    r: D.radius[0] + (D.radius[1] - D.radius[0]) * Math.pow(r(4.3), 1.3),
    ox: (r(5.9) - 0.5) * 12, oy: (r(6.7) - 0.5) * 10,
  }
}
export type DropState = {
  x: number; y: number; vx: number; vy: number
  /** 床に落ち着いた時刻 (生まれてからの秒)。 まだ飛んでいれば null。 */
  restAt: number | null
  /** 床に当たった時刻 (生まれてからの秒) と x。 */
  hits: { t: number; x: number }[]
}
/** 滴の lt 秒後 (生まれてからの秒) の位置。 floor = 接点から床までの高さ (px・下が +)。 */
export function moltenDropAt(s: DropSpec, lt: number, floor: number, P: MoltenParams = moltenP()): DropState {
  const D = P.drop
  let x = s.ox, y = s.oy, vx = s.vx, vy = s.vy
  let restAt: number | null = null
  const hits: { t: number; x: number }[] = []
  const steps = Math.max(0, Math.floor(lt / DT))
  for (let n = 0; n < steps; n++) {
    const t = (n + 1) * DT
    if (restAt !== null) {
      vx *= Math.exp(-14 * DT)
      x += vx * DT
      continue
    }
    const k = Math.exp(-D.drag * DT)
    vx *= k
    vy = vy * k + D.gravity * DT
    x += vx * DT
    y += vy * DT
    if (y >= floor && vy > 0) {
      y = floor
      hits.push({ t, x })
      if (hits.length <= D.bounce && vy > D.minBounce) {
        vy = -vy * D.restitution
        vx *= D.keepX
      } else {
        vy = 0
        vx *= D.keepX
        restAt = t
      }
    }
  }
  return { x, y, vx, vy, restAt, hits }
}

/** 星形の光の濃さ (0〜1) と広がり。 */
export function moltenStarAt(since: number, P: MoltenParams = moltenP()): { alpha: number; grow: number } {
  const S = P.star
  if (since < 0 || since >= S.end) return { alpha: 0, grow: 0 }
  const alpha = since < S.rise ? since / S.rise : since < S.hold ? 1 : Math.pow(1 - (since - S.hold) / (S.end - S.hold), 1.6)
  return { alpha, grow: clamp01(since / S.end) }
}
/** 全体の引き (fadeFrom 秒から MOLTEN_END 秒で 1 → 0)。 */
export function moltenOutro(since: number): number {
  const F = moltenP().fadeFrom
  if (since <= F) return 1
  const u = clamp01((since - F) / (MOLTEN_END - F))
  return 1 - u * u * (3 - 2 * u)
}

/** 先細りの点 (頭 x,y・向き ux,uy・長さ len・太さ w): 頭が丸く、後ろへ細って消える。 */
function tail(ctx: CanvasRenderingContext2D, x: number, y: number, ux: number, uy: number, len: number, w: number, head: RGB, end: RGB, a: number): void {
  const tx = x - ux * len, ty = y - uy * len
  const g = ctx.createLinearGradient(x, y, tx, ty)
  g.addColorStop(0, rgba(head, 0.9 * a))
  g.addColorStop(1, rgba(end, 0))
  ctx.fillStyle = g
  ctx.beginPath()
  ctx.moveTo(x - uy * w, y + ux * w)
  ctx.lineTo(tx, ty)
  ctx.lineTo(x + uy * w, y - ux * w)
  ctx.closePath()
  ctx.fill()
}
function glow(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, stops: readonly [number, RGB, number][], sy = 1): void {
  ctx.save()
  ctx.translate(x, y)
  ctx.scale(1, sy)
  const g = ctx.createRadialGradient(0, 0, 0, 0, 0, r)
  for (const [o, c, a] of stops) g.addColorStop(o, rgba(c, a))
  ctx.fillStyle = g
  ctx.fillRect(-r, -r, r * 2, r * 2)
  ctx.restore()
}

/** 星形の光 (短い先細りの腕 8 本＋白い芯)。 色の多い方の道で外を染める。 */
function drawStar(ctx: CanvasRenderingContext2D, f: Spark, since: number, facing: 1 | -1, ramp: Ramp): void {
  const S = moltenP().star
  const st = moltenStarAt(since)
  if (st.alpha <= 0.01) return
  const a = st.alpha, sc = 0.85 + 0.3 * st.grow
  const outer = sparkRampAt(0.3, ramp)
  for (let k = 0; k < 8; k++) {
    const diag = k % 2 === 1
    // 横の腕を長く、縦の腕は短く (見本の縦の長いすじにしない)。
    const len = (diag ? S.diag : k % 4 === 0 ? S.long : S.short) * sc
    const w = diag ? S.width * 0.6 : S.width
    const ang = (S.tilt + k * Math.PI / 4) * facing
    const ux = Math.cos(ang), uy = Math.sin(ang)
    const tx = f.x + ux * len, ty = f.y + uy * len
    const g = ctx.createLinearGradient(f.x, f.y, tx, ty)
    g.addColorStop(0, rgba(TEHON_COLOR.white, 0.95 * a))
    g.addColorStop(0.35, rgba(outer, 0.55 * a))
    g.addColorStop(1, rgba(outer, 0))
    ctx.fillStyle = g
    ctx.beginPath()
    ctx.moveTo(f.x - uy * w, f.y + ux * w)
    ctx.lineTo(tx, ty)
    ctx.lineTo(f.x + uy * w, f.y - ux * w)
    ctx.closePath()
    ctx.fill()
  }
  glow(ctx, f.x, f.y, S.core * sc, [[0, TEHON_COLOR.white, a], [0.35, TEHON_COLOR.warm, 0.6 * a], [1, outer, 0]])
}

/** 細かい飛沫 (1 コマ目に接点のまわりへ散る)。 */
function drawSpray(ctx: CanvasRenderingContext2D, f: Spark, since: number, facing: 1 | -1, seed: number): void {
  const P = moltenP().spray
  if (since >= P.born + P.life[1]) return
  for (let i = 0; i < P.count; i++) {
    const r = (k: number) => h01(i * k + seed + 53.1)
    const born = P.born * r(1.7)
    const life = P.life[0] + (P.life[1] - P.life[0]) * r(2.3)
    const lt = since - born
    if (lt < 0 || lt >= life) continue
    // 半分強は主人公の側 (後ろ) の半円、残りは全周。
    const deg = r(3.1) < P.back ? (r(3.9) - 0.5) * 160 - 15 : r(4.7) * 360
    const a0 = deg * Math.PI / 180
    const v0 = P.speed[0] + (P.speed[1] - P.speed[0]) * r(5.3)
    const e = Math.exp(-P.drag * lt), d = v0 * (1 - e) / P.drag
    const x = f.x + Math.cos(a0) * d * -facing, y = f.y + Math.sin(a0) * d + 0.5 * P.gravity * lt * lt
    const vx = Math.cos(a0) * v0 * e * -facing, vy = Math.sin(a0) * v0 * e + P.gravity * lt
    const p = lt / life
    const al = Math.min(1, lt / (1 / 60)) * Math.pow(1 - p, 1.2)
    const ramp = sparkIsBlue(i, seed, 5.5) ? BLUE_RAMP : SPARK_RAMP
    const c = sparkRampAt(0.1 + 0.55 * p, ramp), c2 = sparkRampAt(0.4 + 0.5 * p, ramp)
    const size = P.size[0] + (P.size[1] - P.size[0]) * r(6.1)
    const v = Math.hypot(vx, vy)
    if (v > 1) tail(ctx, x, y, vx / v, vy / v, Math.max(2, Math.min(14, v * 0.012)), size, c, c2, al)
    glow(ctx, x, y, size * 2, [[0, sparkRampAt(0.05, ramp), al], [0.45, c, 0.8 * al], [1, c2, 0]])
  }
}

/** 重い滴・床で跳ねる飛沫・床に残って冷める滴。 floorY = 床の世界 y。 */
function drawDrops(ctx: CanvasRenderingContext2D, f: Spark, since: number, facing: 1 | -1, floorY: number, seed: number): void {
  const D = moltenP().drop, SP = moltenP().splash
  const floor = Math.max(10, floorY - f.y)
  const out = moltenOutro(since)
  for (let i = 0; i < D.count; i++) {
    const s = moltenDropSpec(i, seed)
    const lt = since - s.born
    if (lt < 0) continue
    const st = moltenDropAt(s, lt, floor)
    const ramp = sparkIsBlue(i, seed, 7.3) ? BLUE_RAMP : SPARK_RAMP
    const x = f.x - st.x * facing, y = f.y + st.y
    const born = Math.min(1, lt / (1 / 60))
    // 床で跳ねた所の小さな飛沫と、床に一瞬広がる照り (当たるたび)。
    st.hits.forEach((h, n) => {
      const age = lt - h.t
      const hx = f.x - h.x * facing, hy = f.y + floor
      if (age < SP.flash) glow(ctx, hx, hy, 15 * (1 - 0.4 * n), [[0, sparkRampAt(0.15, ramp), 0.55 * (1 - age / SP.flash)], [1, sparkRampAt(0.5, ramp), 0]], 0.26)
      for (let m = 0; m < SP.count - n; m++) {
        const q = (k: number) => h01(i * 13.7 + n * 5.1 + m * k + seed + 71.3)
        const life = SP.life[0] + (SP.life[1] - SP.life[0]) * q(1.9)
        if (age < 0 || age >= life) continue
        const a0 = -Math.PI / 2 + (q(2.7) - 0.5) * 2.2
        const v0 = (SP.speed[0] + (SP.speed[1] - SP.speed[0]) * q(3.3)) * (1 - 0.4 * n)
        const px = hx + Math.cos(a0) * v0 * age, py = hy + Math.sin(a0) * v0 * age + 0.5 * SP.gravity * age * age
        if (py > hy + 1) continue
        const vx = Math.cos(a0) * v0, vy = Math.sin(a0) * v0 + SP.gravity * age, v = Math.hypot(vx, vy)
        const al = Math.min(1, age / (1 / 60)) * (1 - age / life) * out
        const c = sparkRampAt(0.15 + 0.5 * age / life, ramp)
        if (v > 1) tail(ctx, px, py, vx / v, vy / v, Math.min(9, v * 0.014), 0.9, c, sparkRampAt(0.6, ramp), al)
        glow(ctx, px, py, 2, [[0, sparkRampAt(0.05, ramp), al], [1, c, 0]])
      }
    })
    if (st.restAt === null) {
      // 飛んでいる滴: 丸い熱い芯・外のにじみ・動く向きの後ろへ短い尾。 飛ぶほど冷める。
      const cool = clamp01(0.06 + 0.42 * lt / 0.45)
      const al = born * out
      const body = sparkRampAt(cool, ramp), edge = sparkRampAt(cool + 0.3, ramp)
      const vx = -st.vx * facing, vy = st.vy, v = Math.hypot(vx, vy)
      glow(ctx, x, y, s.r * 3.4, [[0, body, 0.32 * al], [0.5, edge, 0.12 * al], [1, edge, 0]])
      if (v > 1) tail(ctx, x, y, vx / v, vy / v, Math.max(s.r, Math.min(22, v * 0.022)), s.r * 0.95, body, edge, al)
      glow(ctx, x, y, s.r * 1.25, [[0, sparkRampAt(cool * 0.4, ramp), al], [0.55, body, 0.95 * al], [1, edge, 0]])
    } else {
      // 床に残った滴: 横に潰れた燠。 冷めて暗くなりながら消える (明滅しない)。
      const rest = lt - st.restAt
      const span = Math.max(0.05, MOLTEN_END - s.born - st.restAt)
      const u = clamp01(rest / span)
      const cool = 0.42 + 0.5 * u
      const al = Math.pow(1 - u, 1.1) * out
      const body = sparkRampAt(cool, ramp), edge = sparkRampAt(cool + 0.25, ramp)
      glow(ctx, x, floorY, s.r * 3, [[0, body, 0.3 * al], [0.6, edge, 0.1 * al], [1, edge, 0]], 0.32)
      glow(ctx, x, floorY - s.r * 0.25, s.r * 1.5, [[0, sparkRampAt(cool * 0.7, ramp), 0.95 * al], [0.6, body, 0.7 * al], [1, edge, 0]], 0.42)
    }
  }
}

/** 形B を描く (?sparkShape=b・b2 の時だけ・世界座標の ctx)。 floorY = 床の世界 y (主人公の足もと)。 */
export function drawTehonMolten(ctx: CanvasRenderingContext2D, f: Spark, since: number, facing: 1 | -1, floorY: number): void {
  if (tehonSparkShape() === 'now' || since < 0 || since >= MOLTEN_END) return
  const seed = tehonSeed(f)
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  drawStar(ctx, f, since, facing, sparkBlueShare() >= 0.5 ? BLUE_RAMP : SPARK_RAMP)
  drawSpray(ctx, f, since, facing, seed)
  drawDrops(ctx, f, since, facing, floorY, seed)
  ctx.restore()
}
